import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Options, Splide, SplideSlide } from '@splidejs/react-splide';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { SmaverseTag } from '@app/components/Product/SmaverseTag';
import { attributesBySmaverseSelector } from '@app/domain/catalog';
import { CAROUSEL_PRODUCT_SIZE } from '@app/static/constants';
import { Product } from '@app/types/catalog';
import { checkSmaverseSupport } from '@app/utils/catalog';
import { getEllipsisStyle } from '@app/utils/format';

interface CarouselProductProps {
  isProperty?: boolean;
  options?: Options;
  products: Array<Product>;
  sx?: SxProps<Theme>;
}

export function CarouselProduct({
  isProperty,
  options,
  products,
  sx,
}: CarouselProductProps) {
  const smaverseAttributes = useRecoilValue(attributesBySmaverseSelector);
  const navigate = useNavigate();
  const extendedProducts =
    products.length < CAROUSEL_PRODUCT_SIZE
      ? Array(Math.ceil(CAROUSEL_PRODUCT_SIZE / products.length))
          .fill(products)
          .flat()
      : products;
  return (
    <Splide
      options={{
        arrows: false,
        autoplay: true,
        breakpoints: {
          768: {
            gap: 10,
            perPage: 1,
          },
        },
        gap: 30,
        interval: 2000,
        padding: { left: '10%', right: '10%' },
        pagination: false,
        pauseOnHover: true,
        perPage: 3,
        type: 'loop',
        ...options,
      }}
    >
      {extendedProducts.map((item, index) => (
        <SplideSlide
          key={index}
          style={{ cursor: 'pointer' }}
          onClick={() =>
            navigate(`/${isProperty ? 'properties' : 'exhibitions'}/${item.id}`)
          }
        >
          <Card
            sx={{
              '&:hover .hoverOverlay': {
                opacity: 0.3,
              },
              borderRadius: 0,
              maxWidth: 500,
              overflow: 'hidden',
              position: 'relative',
              ...sx,
            }}
          >
            {item?.images?.[0]?.url && (
              <CardMedia
                component="img"
                image={item?.images?.[0]?.url}
                alt={item?.name}
                sx={{ objectFit: 'contain' }}
              />
            )}
            <Box
              className="hoverOverlay"
              sx={{
                bgcolor: 'white',
                height: '100%',
                left: 0,
                opacity: 0,
                position: 'absolute',
                top: 0,
                transition: 'opacity 0.3s ease-in-out',
                width: '100%',
                zIndex: 1,
              }}
            />

            <CardContent sx={{ bgcolor: 'white' }}>
              <Stack spacing={1}>
                <Typography variant="body1" fontWeight="bold" noWrap>
                  {item?.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    height: 46,
                    ...getEllipsisStyle(2),
                  }}
                >
                  {item?.description}
                </Typography>
              </Stack>
            </CardContent>
            {checkSmaverseSupport(
              item.attributes || [],
              smaverseAttributes
            ) && (
              <SmaverseTag
                sx={{
                  left: 4,
                  top: 4,
                }}
              />
            )}
          </Card>
        </SplideSlide>
      ))}
    </Splide>
  );
}
